import { jsPDF } from 'jspdf';

/**
 * Generate a comprehensive professional PDF from the tax form data
 * @param {Object} formData - All form data collected from 6 pages
 * @param {string} language - Current language ('en' or 'es')
 * @param {string} confirmationNumber - Confirmation number from database submission
 * @param {Object} organization - Organization data for branding (optional)
 * @returns {jsPDF} - PDF document ready to download or save
 */
export const generateTaxFormPDF = async (formData, language = 'en', confirmationNumber = null, organization = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;
  const leftMargin = 20;
  const rightMargin = pageWidth - 20;
  const lineHeight = 6;

  // Extract organization branding with fallbacks
  const orgName = organization?.name || 'Tax Services';
  const orgLogoUrl = organization?.logo_url || null;
  const orgPrimaryColor = organization?.primary_color || '#2980B9'; // Default blue
  const orgCompanyEmail = organization?.company_email || '';
  const orgCompanyPhone = organization?.company_phone || '';

  // Convert hex color to RGB for jsPDF
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 41, g: 128, b: 185 }; // Default blue if parsing fails
  };

  const primaryColor = hexToRgb(orgPrimaryColor);

  // Use provided confirmation number or generate a temporary one (shouldn't happen)
  const pdfConfirmationNumber = confirmationNumber || 'PENDING';
  const submissionDate = new Date().toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
    dateStyle: 'full',
    timeStyle: 'short'
  });

  // Helper function to check if we need a new page
  const checkNewPage = (spaceNeeded = 20) => {
    if (yPosition + spaceNeeded > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 10, isBold = false, color = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(color[0], color[1], color[2]);
    
    const lines = doc.splitTextToSize(text, pageWidth - 40);
    lines.forEach(line => {
      checkNewPage();
      doc.text(line, leftMargin, yPosition);
      yPosition += lineHeight;
    });
    doc.setTextColor(0, 0, 0);
  };

  // Helper function to add a professional section header (with org color)
  const addSectionHeader = (title) => {
    checkNewPage(15);
    yPosition += 5;
    
    // Use organization's primary color
    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.roundedRect(leftMargin, yPosition - 6, pageWidth - 40, 10, 2, 2, 'F');
    
    // White text on colored background
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(title, leftMargin + 3, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 8;
  };

  // Helper function to add a field with label and value
  const addField = (label, value, inline = false) => {
    if (value && value !== '' && value !== 'undefined') {
      checkNewPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(label + ':', leftMargin, yPosition);
      
      doc.setFont('helvetica', 'normal');
      const labelWidth = doc.getTextWidth(label + ': ');
      
      if (inline && labelWidth + doc.getTextWidth(String(value)) < pageWidth - 50) {
        doc.text(String(value), leftMargin + labelWidth + 3, yPosition);
        yPosition += lineHeight;
      } else {
        yPosition += lineHeight;
        const lines = doc.splitTextToSize(String(value), pageWidth - 50);
        lines.forEach(line => {
          checkNewPage();
          doc.text(line, leftMargin + 5, yPosition);
          yPosition += lineHeight;
        });
      }
    }
  };

  // Helper function to add a divider line
  const addDivider = () => {
    checkNewPage();
    doc.setDrawColor(200, 200, 200);
    doc.line(leftMargin, yPosition, rightMargin, yPosition);
    yPosition += 5;
  };

  try {
    // =========================
    // HEADER WITH ORGANIZATION LOGO
    // =========================
    
    // Add Logo
    if (orgLogoUrl) {
      try {
        console.log('Loading logo from:', orgLogoUrl);
        const logoData = await loadImageAsDataURL(orgLogoUrl);
        const logoWidth = 40;
        const logoHeight = 40;
        const logoX = (pageWidth - logoWidth) / 2;
        doc.addImage(logoData, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 5;
        console.log('✅ Logo added successfully');
      } catch (error) {
        console.error('❌ Could not load logo:', error);
        // Fallback: Show organization name as header if logo fails
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
        doc.text(orgName, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
        doc.setTextColor(0, 0, 0);
      }
    } else {
      // No logo URL provided, show organization name
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text(orgName, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setTextColor(0, 0, 0);
    }

    // Company Name and Title (using organization name)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(language === 'es' ? 'Formulario de Impuestos 2025' : 'Tax Form 2025', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;
    
    // Organization name below title
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(orgName, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Confirmation and Date Box (using org color)
    doc.setFillColor(240, 248, 255);
    doc.roundedRect(leftMargin, yPosition - 3, pageWidth - 40, 16, 2, 2, 'F');
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.roundedRect(leftMargin, yPosition - 3, pageWidth - 40, 16, 2, 2, 'S');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${language === 'es' ? 'Número de Confirmación' : 'Confirmation Number'}: ${pdfConfirmationNumber}`, leftMargin + 3, yPosition);
    yPosition += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`${language === 'es' ? 'Fecha de Envío' : 'Submission Date'}: ${submissionDate}`, leftMargin + 3, yPosition);
    yPosition += 12;

    // =========================
    // PAGE 1: TAXPAYER INFORMATION
    // =========================
    addSectionHeader(language === 'es' ? 'INFORMACIÓN DEL CONTRIBUYENTE' : 'TAXPAYER INFORMATION');
    
    const fullName = `${formData.taxpayerFirstName || ''} ${formData.taxpayerMiddleName || ''} ${formData.taxpayerLastName || ''}`.trim();
    addField(language === 'es' ? 'Nombre Completo' : 'Full Name', fullName, true);
    addField(language === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth', formData.dateOfBirth, true);
    addField(language === 'es' ? 'Estado Civil' : 'Filing Status', formData.filingStatus, true);
    addField(language === 'es' ? 'Fuente de Ingresos' : 'Income Source', formData.incomeSource, true);
    
    yPosition += 3;
    addText(language === 'es' ? 'Dirección:' : 'Address:', 9, true);
    
    // Build address - handle missing street_address gracefully
    const addressParts = [];
    if (formData.streetAddress) {
      addressParts.push(formData.streetAddress);
    }
    if (formData.streetAddress2) {
      addressParts.push(formData.streetAddress2);
    }
    // Always include city, state, zip even if street address is missing
    if (formData.city || formData.state || formData.postalCode) {
      addressParts.push(`${formData.city || ''}, ${formData.state || ''} ${formData.postalCode || ''}`.trim());
    }
    
    const fullAddress = addressParts.filter(Boolean).join(', ') || 'Address not provided';
    addText(fullAddress, 9, false);
    
    yPosition += 2;
    addField(language === 'es' ? 'Teléfono' : 'Phone Number', formData.phoneNumber, true);
    addField(language === 'es' ? 'Correo Electrónico' : 'Email Address', formData.email, true);
    addField(language === 'es' ? 'Número de Seguro Social/ITIN' : 'Social Security Number/ITIN', formData.ssn, true);
    addField(language === 'es' ? 'Licencia de Conducir/ID' : 'Driver\'s License/ID', formData.driversLicense, true);
    addField(language === 'es' ? 'Vencimiento de ID' : 'ID Expiration Date', formData.idExpirationDate, true);

    yPosition += 2;
    addField(language === 'es' ? 'Seguro Médico del Mercado' : 'Marketplace Health Insurance', formData.hasMarketplaceInsurance, true);
    if (formData.hasMarketplaceInsurance === 'No' && formData.wantsHealthInsurance) {
      addField(language === 'es' ? '¿Desea Seguro Médico?' : 'Wants Health Insurance?', formData.wantsHealthInsurance, true);
    }
    
    addField(language === 'es' ? 'Seguro de Vida' : 'Life Insurance', formData.hasLifeInsurance, true);
    if (formData.hasLifeInsurance === 'No' && formData.wantsLifeInsurance) {
      addField(language === 'es' ? '¿Desea Seguro de Vida?' : 'Wants Life Insurance?', formData.wantsLifeInsurance, true);
    }
    
    addField(language === 'es' ? 'Situación de Vivienda' : 'Home Ownership', formData.homeOwnership, true);
    if (formData.homeOwnership === 'Rent' && formData.wantsToBuyHome) {
      addField(language === 'es' ? '¿Desea Comprar Casa?' : 'Wants to Buy Home?', formData.wantsToBuyHome, true);
    }

    // Spouse Information (if applicable)
    if (formData.filingStatus === 'Married Filing Jointly') {
      yPosition += 4;
      addDivider();
      addText(language === 'es' ? 'Información del Cónyuge:' : 'Spouse Information:', 10, true);
      const spouseName = `${formData.spouseFirstName || ''} ${formData.spouseMiddleName || ''} ${formData.spouseLastName || ''}`.trim();
      addField(language === 'es' ? 'Nombre' : 'Name', spouseName, true);
      addField(language === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth', formData.spouseDateOfBirth, true);
      addField(language === 'es' ? 'SSN/ITIN' : 'SSN/ITIN', formData.spouseSsn, true);
      addField(language === 'es' ? 'Licencia' : 'License', formData.spouseDriversLicense, true);
      addField(language === 'es' ? 'Vencimiento ID' : 'ID Expiration', formData.spouseIdExpirationDate, true);
    }

    // Dependents Information
    if (formData.claimingDependents === 'Yes' && formData.dependents && formData.dependents.length > 0) {
      yPosition += 4;
      addDivider();
      addText(language === 'es' ? 'Dependientes:' : 'Dependents:', 10, true);
      addField(language === 'es' ? 'Número de Dependientes' : 'Number of Dependents', formData.numberOfDependents, true);
      
      formData.dependents.forEach((dep, index) => {
        if (dep && dep.firstName) {
          yPosition += 3;
          checkNewPage(35);
          
          // Store starting position for the box
          const boxStartY = yPosition - 3;
          
          // Calculate all content first to determine box height
          const depName = `${dep.firstName || ''} ${dep.middleName || ''} ${dep.lastName || ''}`.trim();
          
          // Draw the box first (as background)
          const boxHeight = 32; // Fixed height for consistent layout
          doc.setFillColor(250, 250, 250);
          doc.roundedRect(leftMargin, boxStartY, pageWidth - 40, boxHeight, 1, 1, 'F');
          doc.setDrawColor(200, 200, 200);
          doc.roundedRect(leftMargin, boxStartY, pageWidth - 40, boxHeight, 1, 1, 'S');
          
          // Now draw text on top of the box
          let fieldY = boxStartY + 6;
          
          // Header
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.text(`${language === 'es' ? 'Dependiente' : 'Dependent'} ${index + 1}`, leftMargin + 2, fieldY);
          fieldY += 6;
          
          // Name
          doc.setFont('helvetica', 'bold');
          doc.text((language === 'es' ? 'Nombre' : 'Name') + ':', leftMargin, fieldY);
          doc.setFont('helvetica', 'normal');
          const nameWidth = doc.getTextWidth((language === 'es' ? 'Nombre' : 'Name') + ': ');
          doc.text(depName, leftMargin + nameWidth + 3, fieldY);
          fieldY += 5;
          
          // Relationship and SSN on same line
          doc.setFont('helvetica', 'bold');
          doc.text((language === 'es' ? 'Relación' : 'Relationship') + ':', leftMargin, fieldY);
          doc.setFont('helvetica', 'normal');
          const relWidth = doc.getTextWidth((language === 'es' ? 'Relación' : 'Relationship') + ': ');
          doc.text(String(dep.relationship || ''), leftMargin + relWidth + 3, fieldY);
          fieldY += 5;
          
          // SSN
          doc.setFont('helvetica', 'bold');
          doc.text('SSN:', leftMargin, fieldY);
          doc.setFont('helvetica', 'normal');
          const ssnWidth = doc.getTextWidth('SSN: ');
          doc.text(String(dep.ssn || ''), leftMargin + ssnWidth + 3, fieldY);
          fieldY += 5;
          
          // DOB and Activity
          doc.setFont('helvetica', 'bold');
          doc.text((language === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth') + ':', leftMargin, fieldY);
          doc.setFont('helvetica', 'normal');
          const dobWidth = doc.getTextWidth((language === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth') + ': ');
          doc.text(String(dep.dateOfBirth || ''), leftMargin + dobWidth + 3, fieldY);
          
          // Activity on same line if there's space
          const activityLabel = (language === 'es' ? 'Actividad' : 'Activity') + ': ';
          const activityX = leftMargin + 90;
          doc.setFont('helvetica', 'bold');
          doc.text(activityLabel, activityX, fieldY);
          doc.setFont('helvetica', 'normal');
          const actWidth = doc.getTextWidth(activityLabel);
          doc.text(String(dep.activity || ''), activityX + actWidth, fieldY);
          
          // Update yPosition to after the box
          yPosition = boxStartY + boxHeight + 1;
        }
      });
    }

    // =========================
    // PAGE 2: QUALIFYING DEPENDENTS (if applicable)
    // =========================
    if (formData.claimingDependents === 'Yes') {
      checkNewPage(40);
      addSectionHeader(language === 'es' ? 'PREGUNTAS SOBRE DEPENDIENTES CALIFICADOS' : 'QUALIFYING DEPENDENT QUESTIONS');
      
      addField(language === 'es' ? 'Ciudadanos/Residentes de EE.UU.' : 'US Citizens/Residents', formData.allDependentsCitizens, true);
      addField(language === 'es' ? 'Única Persona Reclamando' : 'Only Person Claiming', formData.onlyPersonClaiming, true);
      addField(language === 'es' ? 'Presentando Declaración Conjunta' : 'Filing Joint Return', formData.filingJointReturn, true);
      addField(language === 'es' ? 'Relacionados con Usted' : 'Related to You', formData.relatedToYou, true);
      addField(language === 'es' ? 'Cumple Requisito de Edad' : 'Meets Age Requirement', formData.meetsAgeRequirement, true);
      addField(language === 'es' ? 'Vive con Usted' : 'Lives With You', formData.livesWithYou, true);
      addField(language === 'es' ? 'Apoyo Financiero' : 'Financial Support', formData.financiallySupport, true);
      addField(language === 'es' ? 'Factura de Servicios' : 'Utility Bill', formData.hasUtilityBill, true);
      addField(language === 'es' ? 'Ingresos Menos de $4,200' : 'Income Less Than $4,200', formData.dependentIncomeLessThan4200, true);
      addField(language === 'es' ? 'Gastos de Cuidado Infantil' : 'Child Care Expenses', formData.childCareExpenses, true);
    }

    // =========================
    // PAGE 3: HOUSEHOLD EMPLOYEE (if applicable)
    // =========================
    if (formData.householdEmployeeAcknowledged || formData.recordKeepingAcknowledged) {
      checkNewPage(30);
      addSectionHeader(language === 'es' ? 'EMPLEADO DOMÉSTICO/TRABAJADOR POR CUENTA PROPIA' : 'HOUSEHOLD EMPLOYEE/SELF-EMPLOYED');
      
      // Special handling for long labels - force them to separate lines
      if (formData.householdEmployeeAcknowledged) {
        checkNewPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(language === 'es' ? 'Reconocimiento Empleado Doméstico:' : 'Household Employee Acknowledgement:', leftMargin, yPosition);
        yPosition += lineHeight;
        doc.setFont('helvetica', 'normal');
        doc.text(language === 'es' ? 'Reconocido' : 'Acknowledged', leftMargin + 5, yPosition);
        yPosition += lineHeight;
      }
      
      if (formData.recordKeepingAcknowledged) {
        checkNewPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(language === 'es' ? 'Reconocimiento Mantenimiento de Registros:' : 'Record Keeping Acknowledgement:', leftMargin, yPosition);
        yPosition += lineHeight;
        doc.setFont('helvetica', 'normal');
        doc.text(language === 'es' ? 'Reconocido' : 'Acknowledged', leftMargin + 5, yPosition);
        yPosition += lineHeight;
      }
    }

    // =========================
    // PAGE 4: SCHEDULE C WORKSHEET (if applicable)
    // =========================
    if (formData.scheduleC && Object.keys(formData.scheduleC).length > 0) {
      const sc = formData.scheduleC;
      
      checkNewPage(40);
      addSectionHeader(language === 'es' ? 'HOJA DE TRABAJO DEL ANEXO C' : 'SCHEDULE C WORKSHEET');
      
      // Business Information
      if (sc.businessName || sc.businessAddress || sc.firstYearInBusiness) {
        addText(language === 'es' ? 'Información del Negocio:' : 'Business Information:', 10, true);
        addField(language === 'es' ? 'Nombre del Negocio' : 'Business Name', sc.businessName, true);
        addField(language === 'es' ? 'Dirección del Negocio' : 'Business Address', sc.businessAddress, true);
        addField(language === 'es' ? 'Primer Año en el Negocio' : 'First Year in Business', sc.firstYearInBusiness, true);
        addField(language === 'es' ? 'ID Federal' : 'Federal ID', sc.federalId, true);
        addField(language === 'es' ? 'Hizo Pagos que Requieren 1099' : 'Made 1099 Payments', sc.madePaymentsRequiring1099, true);
        if (sc.madePaymentsRequiring1099 === 'Yes') {
          addField(language === 'es' ? 'Presentó 1099' : 'Filed 1099', sc.filed1099, true);
        }
        yPosition += 3;
      }
      
      // Income
      if (sc.totalGrossIncome) {
        addDivider();
        addText(language === 'es' ? 'Ingresos:' : 'Income:', 10, true);
        addField(language === 'es' ? 'Ingreso Bruto Total del Negocio' : 'Total Gross Business Income', sc.totalGrossIncome, true);
        yPosition += 3;
      }
      
      // Retail Inventory
      if (sc.isRetailBusiness === 'Yes') {
        addDivider();
        addText(language === 'es' ? 'Inventario Minorista:' : 'Retail Inventory:', 10, true);
        addField(language === 'es' ? 'Inventario Inicial' : 'Beginning Inventory', sc.beginningInventory, true);
        addField(language === 'es' ? 'Mercancía Comprada' : 'Merchandise Purchased', sc.merchandisePurchased, true);
        addField(language === 'es' ? 'Costo de Mano de Obra' : 'Cost of Labor', sc.costOfLabor, true);
        addField(language === 'es' ? 'Materiales y Suministros' : 'Materials & Supplies', sc.materialsSupplies, true);
        addField(language === 'es' ? 'Otros Costos Directos' : 'Other Direct Costs', sc.otherDirectSalesCosts, true);
        addField(language === 'es' ? 'Inventario Final' : 'Ending Inventory', sc.endingInventory, true);
        yPosition += 3;
      }
      
      // Business Expenses
      const hasAnyExpenses = sc.advertising || sc.repairsAndMaintenance || sc.commissionsAndFees || 
        sc.suppliesNotIncludedAbove || sc.contractLabor || sc.realEstateTaxesBusiness ||
        sc.insuranceOtherThanHealth || sc.otherTaxesPayroll || sc.healthInsuranceSelf ||
        sc.travel || sc.healthInsuranceEmployees || sc.mealsAndEntertainment ||
        sc.mortgageInterestBusiness || sc.utilities || sc.otherInterestPaid || sc.wagesW2Issued ||
        sc.professionalFees || sc.bankAndCCCharges || sc.officeExpenses || sc.tools ||
        sc.rentOnBusinessProperty || sc.uniformsLogo || sc.equipmentRentals || sc.licenseDues || sc.telephone;
      
      if (hasAnyExpenses) {
        addDivider();
        addText(language === 'es' ? 'Gastos Comerciales:' : 'Business Expenses:', 10, true);
        
        addField(language === 'es' ? 'Publicidad' : 'Advertising', sc.advertising, true);
        addField(language === 'es' ? 'Reparaciones y Mantenimiento' : 'Repairs & Maintenance', sc.repairsAndMaintenance, true);
        addField(language === 'es' ? 'Comisiones y Honorarios' : 'Commissions & Fees', sc.commissionsAndFees, true);
        addField(language === 'es' ? 'Suministros' : 'Supplies', sc.suppliesNotIncludedAbove, true);
        addField(language === 'es' ? 'Mano de Obra Contratada' : 'Contract Labor', sc.contractLabor, true);
        addField(language === 'es' ? 'Impuestos Inmobiliarios' : 'Real Estate Taxes', sc.realEstateTaxesBusiness, true);
        addField(language === 'es' ? 'Seguro (no de salud)' : 'Insurance (other than health)', sc.insuranceOtherThanHealth, true);
        addField(language === 'es' ? 'Otros Impuestos' : 'Other Taxes', sc.otherTaxesPayroll, true);
        addField(language === 'es' ? 'Seguro de Salud (propio)' : 'Health Insurance (self)', sc.healthInsuranceSelf, true);
        addField(language === 'es' ? 'Viajes' : 'Travel', sc.travel, true);
        addField(language === 'es' ? 'Seguro de Salud (empleados)' : 'Health Insurance (employees)', sc.healthInsuranceEmployees, true);
        addField(language === 'es' ? 'Comidas y Entretenimiento' : 'Meals & Entertainment', sc.mealsAndEntertainment, true);
        addField(language === 'es' ? 'Intereses Hipotecarios' : 'Mortgage Interest', sc.mortgageInterestBusiness, true);
        addField(language === 'es' ? 'Servicios Públicos' : 'Utilities', sc.utilities, true);
        addField(language === 'es' ? 'Otros Intereses' : 'Other Interest', sc.otherInterestPaid, true);
        addField(language === 'es' ? 'Salarios' : 'Wages', sc.wagesW2Issued, true);
        addField(language === 'es' ? 'Honorarios Profesionales' : 'Professional Fees', sc.professionalFees, true);
        addField(language === 'es' ? 'Cargos Bancarios' : 'Bank Charges', sc.bankAndCCCharges, true);
        addField(language === 'es' ? 'Gastos de Oficina' : 'Office Expenses', sc.officeExpenses, true);
        addField(language === 'es' ? 'Herramientas' : 'Tools', sc.tools, true);
        addField(language === 'es' ? 'Alquiler de Propiedad' : 'Rent on Property', sc.rentOnBusinessProperty, true);
        addField(language === 'es' ? 'Uniformes y Logo' : 'Uniforms & Logo', sc.uniformsLogo, true);
        addField(language === 'es' ? 'Alquiler de Equipos' : 'Equipment Rentals', sc.equipmentRentals, true);
        addField(language === 'es' ? 'Licencias/Cuotas' : 'License/Dues', sc.licenseDues, true);
        if (sc.telephone) {
          addField(language === 'es' ? 'Teléfono' : 'Telephone', `${sc.telephone} (${sc.telephonePercentBusiness || '0'}% business)`, true);
        }
        
        // Other Expenses
        if (sc.otherExpenses && sc.otherExpenses.length > 0) {
          yPosition += 2;
          addText(language === 'es' ? 'Otros Gastos:' : 'Other Expenses:', 9, true);
          sc.otherExpenses.forEach((exp, idx) => {
            if (exp.description && exp.amount) {
              addField(`${idx + 1}. ${exp.description}`, exp.amount, true);
            }
          });
        }
        
        yPosition += 3;
      }
      
      // Mileage
      if (sc.businessMileage || sc.personalMileage) {
        addDivider();
        addText(language === 'es' ? 'Kilometraje del Negocio:' : 'Business Mileage:', 10, true);
        addField(language === 'es' ? 'Kilometraje de Negocio' : 'Business Mileage', sc.businessMileage, true);
        addField(language === 'es' ? 'Kilometraje Personal' : 'Personal Mileage', sc.personalMileage, true);
        addField(language === 'es' ? '¿Tiene Evidencia?' : 'Has Evidence?', sc.hasEvidenceForMileage, true);
        if (sc.hasEvidenceForMileage === 'Yes') {
          addField(language === 'es' ? '¿Evidencia Por Escrito?' : 'Evidence Written?', sc.isEvidenceWritten, true);
        }
        yPosition += 3;
      }
      
      // Equipment Purchases
      if (sc.purchasedMajorEquipment === 'Yes' && sc.equipment && sc.equipment.length > 0) {
        addDivider();
        addText(language === 'es' ? 'Compras de Equipo:' : 'Equipment Purchases:', 10, true);
        sc.equipment.forEach((equip, idx) => {
          if (equip.name) {
            checkNewPage(12);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.text(`${idx + 1}. ${equip.name}`, leftMargin, yPosition);
            yPosition += lineHeight;
            doc.setFont('helvetica', 'normal');
            doc.text(`   Date: ${equip.date || 'N/A'} | Amount: ${equip.amount || 'N/A'}`, leftMargin, yPosition);
            yPosition += lineHeight;
          }
        });
        yPosition += 3;
      }
      
      // Home Office
      if (sc.hasHomeOffice === 'Yes') {
        addDivider();
        addText(language === 'es' ? 'Oficina en Casa:' : 'Home Office:', 10, true);
        addField(language === 'es' ? 'Pies Cuadrados de Oficina' : 'Office Square Feet', sc.officeSquareFeet, true);
        addField(language === 'es' ? 'Pies Cuadrados de Casa' : 'Home Square Feet', sc.homeSquareFeet, true);
        addField(language === 'es' ? 'Impuestos Inmobiliarios' : 'Real Estate Taxes', sc.realEstateTaxesHome, true);
        addField(language === 'es' ? 'Intereses Hipotecarios/Alquiler' : 'Mortgage Interest/Rent', sc.mortgageInterestOrRent, true);
        addField(language === 'es' ? 'Seguro de Vivienda' : 'Homeowners Insurance', sc.homeownersInsurance, true);
        addField(language === 'es' ? 'Servicios (Luz y Gas)' : 'Utilities (Lights & Gas)', sc.utilitiesLightsGas, true);
      }
    }

    // =========================
    // PAGE 5: FEE AUTHORIZATION
    // =========================
    checkNewPage(30);
    addSectionHeader(language === 'es' ? 'AUTORIZACIÓN DE TARIFAS' : 'FEE AUTHORIZATION');
    
    addField(language === 'es' ? 'Nombre Completo' : 'Full Name', formData.feesName, true);
    addField(language === 'es' ? 'Número de Seguro Social' : 'Social Security Number', formData.feesSsn, true);
    
    yPosition += 3;
    addText(language === 'es' 
      ? `El contribuyente autoriza por la presente a ${orgName} a cobrar todas las tarifas necesarias de preparación de impuestos y tarifas bancarias de mi reembolso de impuestos de 2025.`
      : `The taxpayer hereby authorizes ${orgName} to collect all necessary tax preparation fees and bank fees from my 2025 tax refund.`, 
      9, false);

    // =========================
    // PAGE 6: DISCLOSURE & SIGNATURE
    // =========================
    checkNewPage(60);
    addSectionHeader(language === 'es' ? 'DIVULGACIÓN DE CONSENTIMIENTO Y FIRMA' : 'DISCLOSURE OF CONSENT AND SIGNATURE');
    
    const disclosureName = `${formData.disclosureTaxpayerFirstName || ''} ${formData.disclosureTaxpayerMiddleName || ''} ${formData.disclosureTaxpayerLastName || ''}`.trim();
    addField(language === 'es' ? 'Nombre del Contribuyente' : 'Taxpayer Name', disclosureName, true);
    addField(language === 'es' ? 'Fecha de Firma' : 'Signature Date', formData.signatureDate, true);
    
    // Add signature image if available
    if (formData.signature) {
      console.log('Signature found in formData, length:', formData.signature?.length);
      yPosition += 5;
      addText(language === 'es' ? 'Firma del Contribuyente:' : 'Taxpayer Signature:', 10, true);
      yPosition += 2;
      
      try {
        // Add a border for the signature
        doc.setDrawColor(200, 200, 200);
        doc.rect(leftMargin, yPosition, 80, 25);
        doc.addImage(formData.signature, 'PNG', leftMargin + 2, yPosition + 2, 76, 21);
        yPosition += 30;
        console.log('Signature added to PDF successfully');
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        addText(language === 'es' ? '[Firma Electrónica Capturada]' : '[Electronic Signature Captured]', 9, true);
        yPosition += 5;
      }
    } else {
      console.warn('No signature found in formData');
    }

    // Uploaded Documents - List only (originals stored in documents bucket)
    // ENTERPRISE-LEVEL VALIDATION: Filter out any non-user files (e.g., organization logos)
    const actualUserUploadedFiles = (formData.uploadedFiles || []).filter(file => {
      // Validate this is a legitimate user-uploaded file
      // Exclude files that don't have standard upload properties
      const hasValidName = file && file.name && typeof file.name === 'string';
      const hasValidSize = file && typeof file.size === 'number' && file.size > 0;
      const hasValidType = file && file.type && typeof file.type === 'string';
      
      // Additional check: Exclude files with organization logo names or URLs in their names
      const orgSlug = organization?.slug?.toLowerCase() || '';
      const isNotLogoFile = file.name &&
        !file.name.toLowerCase().includes('logo') &&
        !(orgSlug && file.name.toLowerCase().includes(orgSlug)) &&
        !file.name.toLowerCase().startsWith('http');
      
      return hasValidName && hasValidSize && hasValidType && isNotLogoFile;
    });
    
    if (actualUserUploadedFiles.length > 0) {
      checkNewPage(30);
      addSectionHeader(language === 'es' ? 'DOCUMENTOS ADJUNTOS' : 'ATTACHED DOCUMENTS');
      
      // Add informational text
      addText(
        language === 'es'
          ? 'Los siguientes documentos fueron adjuntados con esta solicitud. Los archivos originales en alta calidad están almacenados de forma segura en nuestro sistema.'
          : 'The following documents were attached with this submission. The original high-quality files are securely stored in our system.',
        9,
        false,
        [80, 80, 80]
      );
      yPosition += 3;
      
      // List each document with details
      actualUserUploadedFiles.forEach((file, index) => {
        checkNewPage(15);
        
        // Document box background
        const boxStartY = yPosition - 2;
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(leftMargin, boxStartY, pageWidth - 40, 10, 1, 1, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(leftMargin, boxStartY, pageWidth - 40, 10, 1, 1, 'S');
        
        // Document number and name
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(`${index + 1}.`, leftMargin + 2, yPosition + 3);
        
        doc.setFont('helvetica', 'normal');
        // Safely handle file name length
        const displayName = file.name.length > 50 ? file.name.substring(0, 47) + '...' : file.name;
        doc.text(displayName, leftMargin + 8, yPosition + 3);
        
        // File type and size
        const fileSize = file.size >= 1024 * 1024 
          ? (file.size / (1024 * 1024)).toFixed(2) + ' MB'
          : (file.size / 1024).toFixed(1) + ' KB';
        const fileType = file.type ? file.type.split('/')[1]?.toUpperCase() : 'FILE';
        const fileInfo = `(${fileType} - ${fileSize})`;
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(8);
        doc.text(fileInfo, leftMargin + 8, yPosition + 7);
        doc.setTextColor(0, 0, 0);
        
        yPosition += 12;
      });
      
      yPosition += 3;
      
      // Add storage location note
      doc.setFillColor(255, 248, 220);
      doc.roundedRect(leftMargin, yPosition - 2, pageWidth - 40, 12, 1, 1, 'F');
      doc.setDrawColor(255, 193, 7);
      doc.roundedRect(leftMargin, yPosition - 2, pageWidth - 40, 12, 1, 1, 'S');
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(
        language === 'es' ? 'Ubicación de Almacenamiento:' : 'Storage Location:',
        leftMargin + 2,
        yPosition + 2
      );
      
      doc.setFont('helvetica', 'normal');
      const lastName = formData.taxpayerLastName || formData.disclosureTaxpayerLastName || 'Unknown';
      const firstName = formData.taxpayerFirstName || formData.disclosureTaxpayerFirstName || '';
      const folderName = `${lastName}_${firstName}_${pdfConfirmationNumber}`;
      
      doc.text(
        language === 'es'
          ? `Carpeta del Cliente: ${folderName}`
          : `Client Folder: ${folderName}`,
        leftMargin + 2,
        yPosition + 6
      );
      
      yPosition += 14;
    }

    // =========================
    // FOOTER ON EVERY PAGE (with organization info)
    // =========================
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      
      // First line: Page number and organization name
      doc.text(
        language === 'es' 
          ? `Página ${i} de ${totalPages} | Generado por ${orgName} | Confidencial`
          : `Page ${i} of ${totalPages} | Generated by ${orgName} | Confidential`,
        pageWidth / 2,
        pageHeight - 13,
        { align: 'center' }
      );
      
      // Second line: Contact info (if available)
      if (orgCompanyEmail || orgCompanyPhone) {
        const contactInfo = [orgCompanyEmail, orgCompanyPhone].filter(Boolean).join(' • ');
        doc.text(
          contactInfo,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }
    }
    
    return doc;

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Helper function to load an image from URL and convert to data URL
 * Handles CORS, format conversion (WebP -> PNG/JPEG), and errors gracefully
 */
const loadImageAsDataURL = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }

    console.log('Attempting to load image:', url);
    
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // Always use CORS for Supabase storage
    
    img.onload = () => {
      try {
        console.log('✅ Image loaded successfully, dimensions:', img.width, 'x', img.height);
        
        // Create canvas and resize to optimal dimensions for PDF
        const canvas = document.createElement('canvas');
        const targetSize = 120;
        canvas.width = targetSize;
        canvas.height = targetSize;
        
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Fill with white background (in case of transparency issues)
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, targetSize, targetSize);
        
        // Calculate dimensions to maintain aspect ratio
        const scale = Math.min(targetSize / img.width, targetSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = (targetSize - scaledWidth) / 2;
        const y = (targetSize - scaledHeight) / 2;
        
        // Draw image centered and scaled
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        // Always convert to PNG for maximum compatibility with jsPDF
        const dataURL = canvas.toDataURL('image/png', 1.0);
        console.log('✅ Image converted to data URL successfully');
        resolve(dataURL);
      } catch (error) {
        console.error('❌ Error processing image:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('❌ Failed to load image from URL:', url);
      console.error('Error details:', error);
      reject(new Error(`Failed to load image: ${url}`));
    };
    
    // Start loading
    img.src = url;
  });
};

// Note: Image embedding functions removed - original files are stored in Supabase 'documents' bucket
// PDF now only lists document names and references the storage location

/**
 * Generate filename for the PDF
 * @param {Object} formData - Form data
 * @param {string} confirmationNumber - Confirmation number from database
 * @returns {string} - Formatted filename
 */
export const generatePDFFilename = (formData, confirmationNumber = null) => {
  // Remove spaces from last name to match storage folder format
  const lastName = (formData.taxpayerLastName || formData.disclosureTaxpayerLastName || 'Unknown').replace(/\s+/g, '');
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const confirmationId = confirmationNumber || 'PENDING';
  
  return `TaxForm_${lastName}_${date}_${confirmationId}.pdf`;
};

/**
 * Download the PDF to the user's device
 * @param {jsPDF} doc - PDF document
 * @param {string} filename - Filename to save as
 */
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};